import { lazy, Suspense, type ReactNode, useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Login } from './pages/Login';
// Signup.tsx is intentionally retained in the codebase but unreachable from
// the live UI — all new accounts are now created via the marketing-side
// /pay flow that paywalls signup behind Stripe checkout. See SignupDisabled
// below for the gate that runs in its place at /signup.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Signup as _Signup } from './pages/Signup';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { VerifyEmail } from './pages/VerifyEmail';
import { NavTopbar } from './components/NavTopbar';
import { currentUser, isAuthenticated, refreshUser } from './lib/auth';

/**
 * Route-level code splitting.
 *
 * Every authed page is a lazy chunk so the initial Login bundle stays under
 * ~300 KB gzip. Pages are named exports, so we map `.then(m => ({ default: m.X }))`.
 * Login is kept eager — the auth gate redirects unauthed users there and we
 * don't want a flash of loading on the very first paint.
 */
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Catalog = lazy(() => import('./pages/Catalog').then((m) => ({ default: m.Catalog })));
const Course = lazy(() => import('./pages/Course').then((m) => ({ default: m.Course })));
const Lesson = lazy(() => import('./pages/Lesson').then((m) => ({ default: m.Lesson })));
const Quiz = lazy(() => import('./pages/Quiz').then((m) => ({ default: m.Quiz })));
const Upgrade = lazy(() => import('./pages/Upgrade').then((m) => ({ default: m.Upgrade })));
const Account = lazy(() => import('./pages/Account').then((m) => ({ default: m.Account })));
const Notes = lazy(() => import('./pages/Notes').then((m) => ({ default: m.Notes })));
const Cohort = lazy(() => import('./pages/Cohort').then((m) => ({ default: m.Cohort })));
const Capstone = lazy(() => import('./pages/Capstone').then((m) => ({ default: m.Capstone })));
const AdminCapstones = lazy(() =>
  import('./pages/AdminCapstones').then((m) => ({ default: m.AdminCapstones })),
);
const AdminAbuse = lazy(() =>
  import('./pages/AdminAbuse').then((m) => ({ default: m.AdminAbuse })),
);
const Verify = lazy(() => import('./pages/Verify').then((m) => ({ default: m.Verify })));

/**
 * Minimal loading shell shown while a lazy route chunk is fetching. Deliberately
 * understated — no spinner, no skeleton — because the chunks are small and the
 * flash should be barely perceptible on a warm cache.
 */
/**
 * The /signup page is paywalled — account creation now happens inside the
 * marketing-side /pay flow (quiz → email/password + Stripe Checkout in one
 * step). Anyone who lands on /signup directly gets bounced to the quiz so
 * they enter through the supported funnel.
 *
 * Kept as a tiny in-route component instead of a top-level Navigate so we
 * can show a brief message during the redirect, and so future variations
 * (e.g. allow-list a referrer) are localised here.
 */
function SignupDisabled() {
  useEffect(() => {
    const t = window.setTimeout(() => {
      window.location.replace('https://thren.io/quiz.html');
    }, 60);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="max-w-md text-center">
        <p className="text-[12px] uppercase tracking-wider text-muted font-semibold">
          Signup is via the quiz
        </p>
        <h1 className="mt-2 text-[24px] font-bold tracking-tight text-ink">
          Redirecting you to start your plan…
        </h1>
        <p className="mt-3 text-[14px] text-muted">
          If you’re not redirected,{' '}
          <a
            className="text-accent hover:underline"
            href="https://thren.io/quiz.html"
          >
            take the 28-day quiz
          </a>{' '}
          to get started.
        </p>
      </div>
    </div>
  );
}

function LoadingShell() {
  return (
    <div
      aria-live="polite"
      aria-busy="true"
      className="min-h-[40vh] flex items-center justify-center text-[12.5px] text-muted"
    >
      Loading…
    </div>
  );
}

/**
 * Lightweight auth guard. Backed by the real bcrypt+JWT session (see
 * `lib/auth.ts`). On miss we redirect to /login with a `?next=` query so the
 * sign-in screen can bounce back to the originally-requested page — matching
 * the same convention used by `api.ts`'s 401 handler.
 *
 * NOTE (v1): we intentionally do NOT require `emailVerified` here. Users can
 * use the app immediately after sign-up and verify whenever convenient. If we
 * later decide some routes need a hard verified gate, that's a per-route
 * wrapper, not a change to this one.
 */
function Protected({ children }: { children: ReactNode }) {
  const location = useLocation();
  if (!isAuthenticated()) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/**
 * Paywall — paid-only product. Users with `plan === 'free'` (either a legacy
 * account or one whose Stripe Checkout never completed) get bounced to
 * `/upgrade` from every protected route except the ones they need to
 * actually finish their purchase: /upgrade itself, /account (billing info),
 * and /verify-email (so email verification can still complete).
 *
 * Sits inside `Protected` so the auth gate runs first. The plan check is
 * deliberately string-equal to 'free' (not !== 'access') so legacy
 * grandfathered tiers (`starter`, `all_access`, `teams`) still pass.
 */
const PAYWALL_ALLOWLIST = new Set(['/account', '/upgrade', '/verify-email']);

function Paywall({ children }: { children: ReactNode }) {
  const location = useLocation();
  const path = location.pathname;
  const search = location.search;
  // Allow nested /account/* and /upgrade/* too, just in case they grow.
  const allowed =
    PAYWALL_ALLOWLIST.has(path) ||
    path.startsWith('/account/') ||
    path.startsWith('/upgrade/');

  // Detect post-Stripe redirect. When `billing=success` is in the query, we
  // need to refresh the cached user — the JWT was signed at signup with
  // plan='free' and the webhook only updates the DB, not the client cache.
  // Without this, a freshly-paid learner gets bounced to /upgrade and thinks
  // their payment was lost. The webhook is also async — there's a race
  // between the Stripe success_url redirect and the webhook landing — so
  // we retry the refresh a few times with backoff to cover the gap.
  const hasBillingSuccess = search.includes('billing=success');
  const [refreshState, setRefreshState] = useState<'idle' | 'refreshing' | 'done'>(
    hasBillingSuccess ? 'refreshing' : 'idle'
  );

  useEffect(() => {
    if (refreshState !== 'refreshing') return;
    let cancelled = false;
    (async () => {
      // Up to 6 attempts over ~15s. Webhook lands within 1-3s in our test
      // E2E; this gives plenty of slack for slower paths without making
      // the user stare at a blank screen too long.
      const delays = [500, 1500, 2500, 3500, 4000, 4000];
      for (const delay of delays) {
        if (cancelled) return;
        try {
          await refreshUser();
          const u = currentUser();
          if (u && u.plan !== 'free') break;
        } catch { /* keep retrying */ }
        await new Promise((r) => setTimeout(r, delay));
      }
      if (!cancelled) setRefreshState('done');
    })();
    return () => { cancelled = true; };
  }, [refreshState]);

  // While refreshing, show a minimal loading state INSTEAD of bouncing to
  // /upgrade. This prevents the "I paid but it bounced me to upgrade" UX.
  if (refreshState === 'refreshing') {
    return (
      <div className="min-h-[60vh] grid place-items-center text-muted">
        <div className="text-center space-y-2">
          <div className="text-sm">Confirming your payment…</div>
          <div className="text-xs opacity-60">Usually takes 2-3 seconds.</div>
        </div>
      </div>
    );
  }

  const user = currentUser();

  // Soft fallback: if the user just paid (`billing=success` was in the URL)
  // but our refresh polling timed out without seeing plan flip away from
  // 'free', the webhook is delayed past 15s. Don't bounce to /upgrade — that
  // makes a paid customer think their money vanished. Show a card with
  // Refresh + support email instead. The webhook will land eventually and
  // a manual refresh will pick it up.
  if (hasBillingSuccess && refreshState === 'done' && user && user.plan === 'free') {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6">
        <div className="surface max-w-[480px] p-7 text-center space-y-3">
          <h1 className="text-[20px] font-semibold text-ink">Payment received — still processing</h1>
          <p className="text-[13.5px] text-muted leading-relaxed">
            Stripe took your payment but our side hasn’t finished provisioning your account yet.
            This usually clears within a minute. Refresh the page, or email{' '}
            <a href="mailto:hello@thren.io" className="text-accent hover:underline">hello@thren.io</a>{' '}
            if it still says this after a few minutes — we’ll fix it manually with your Stripe receipt.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-primary mt-2"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  if (user && user.plan === 'free' && !allowed) {
    return <Navigate to="/upgrade" replace />;
  }
  return <>{children}</>;
}

/**
 * Layout for all authenticated pages — floating pill nav + aurora bg + footer.
 * Public routes (/login, /verify/:id) render their own chrome.
 */
function AuthedLayout() {
  return (
    <div className="min-h-screen relative overflow-x-hidden">
      <a href="#main-content" className="skip-link">Skip to content</a>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px]  opacity-70"
        style={{ animation: 'auroraShift 22s ease-in-out infinite' }}
      />
      <NavTopbar />
      <main id="main-content" tabIndex={-1} className="relative container-x py-6 lg:py-10 focus:outline-none">
        <Suspense fallback={<LoadingShell />}>
          <Outlet />
        </Suspense>
      </main>
      <footer className="container-x pb-10 pt-6 text-xs text-muted flex items-center justify-between">
        <span>© {new Date().getFullYear()} Thren</span>
        <span className="hidden sm:inline">
          Need help?{' '}
          <a className="text-accent hover:underline" href="mailto:hello@thren.io">
            hello@thren.io
          </a>
        </span>
      </footer>
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<LoadingShell />}>
      <Routes>
        {/* Fully public — no nav, no auth. All auth-gate pages are eager so
            the first paint after a redirect is instant (no chunk fetch). */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignupDisabled />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify/:certId" element={<Verify />} />
        <Route path="/quiz" element={<Quiz />} />
        {/* Hidden — no nav link. Instructors visit /admin/capstones directly
            and authenticate with the shared admin key. */}
        <Route path="/admin/capstones" element={<AdminCapstones />} />
        {/* Hidden — operator-only rate-limit abuse dashboard. Same shared
            admin-key gate as /admin/capstones. */}
        <Route path="/admin/abuse" element={<AdminAbuse />} />

        {/* Authed shell */}
        <Route
          element={
            <Protected>
              <Paywall>
                <AuthedLayout />
              </Paywall>
            </Protected>
          }
        >
          <Route index element={<Dashboard />} />
          {/* Explicit /dashboard alias — auth flows (signin, signup,
              verify-email) navigate here by name, so a named path is
              friendlier than the bare index route. */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/courses" element={<Catalog />} />
          <Route path="/notes" element={<Notes />} />
          <Route path="/course/:slug" element={<Course />} />
          <Route path="/course/:slug/capstone" element={<Capstone />} />
          {/*
           * Stepped lesson player. Step is encoded as a query param: `?step=hook`,
           * `?step=concept-0`, `?step=concept-0-1`, `?step=sandbox`, etc.
           * Chosen over a path param so deep-links can omit it and the player
           * resumes the learner's last viewed step from lessonProgress.
           */}
          <Route path="/lesson/:courseSlug/:lessonId" element={<Lesson />} />
          <Route path="/cohort" element={<Cohort />} />
          <Route path="/account" element={<Account />} />
          <Route path="/upgrade" element={<Upgrade />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
